import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

const APPLICATION_TITLE = "Asset Dedication, LLC";
const APPLICATION_DESCRIPTION = "Asset Dedication, LLC";

export interface PageMetadataProps {
  description?: string;
  title?: string;
}

type Registry = {
  present: boolean;
  remove: (key: symbol) => void;
  upsert: (key: symbol, metadata: PageMetadataProps) => void;
};

const PageMetadataRegistry = createContext<Registry>({
  present: false,
  remove: () => {},
  upsert: () => {},
});

type RegisteredPage = {
  key: symbol;
  metadata: PageMetadataProps;
};

// react-helmet-async 3.0.0 detects React 19 and switches to a `React19Dispatcher` that renders its
// tags as elements for React's native document-metadata hoisting, instead of writing to
// `document.head` imperatively. Hoisting accumulates: React does not resolve precedence between two
// `<meta name="description">` elements the way the legacy dispatcher did. The previous design — a
// provider-level `<Helmet>` carrying application defaults plus a page-level `<Helmet>` carrying
// overrides — depended on that precedence, so under React 19 the head ended up with two titles and
// two descriptions and `document.head.querySelector` returned the provider's stale description.
//
// The fix is to stop depending on override semantics. Pages register their metadata and the provider
// renders exactly one resolved `<Helmet>`, which behaves identically on React 18 and React 19.
export function PageMetadataProvider({ children }: PropsWithChildren) {
  const [pages, setPages] = useState<RegisteredPage[]>([]);

  const upsert = useCallback((key: symbol, metadata: PageMetadataProps) => {
    setPages((current) => {
      const index = current.findIndex((page) => page.key === key);
      if (index === -1) {
        return [...current, { key, metadata }];
      }
      const next = [...current];
      next[index] = { key, metadata };
      return next;
    });
  }, []);

  const remove = useCallback((key: symbol) => {
    setPages((current) => current.filter((page) => page.key !== key));
  }, []);

  const registry = useMemo<Registry>(() => ({ present: true, remove, upsert }), [remove, upsert]);

  // Tree registration order, rather than last-update time, defines precedence. Updating a parent
  // page in place must not move it after a still-mounted child and steal the child's metadata.
  const active = pages[pages.length - 1]?.metadata;
  const title = active?.title;
  const description = active?.description ?? APPLICATION_DESCRIPTION;

  return (
    <HelmetProvider>
      <PageMetadataRegistry.Provider value={registry}>
        <Helmet
          meta={[{ content: description, name: "description" }]}
          title={title ?? APPLICATION_TITLE}
          titleTemplate={title ? "%s" : `%s | ${APPLICATION_TITLE}`}
        />
        {children}
      </PageMetadataRegistry.Provider>
    </HelmetProvider>
  );
}

export default function PageMetadata({ description, title }: PageMetadataProps) {
  const { present, remove, upsert } = useContext(PageMetadataRegistry);
  const registrationKey = useRef(Symbol("PageMetadata")).current;
  const latestMetadata = useRef<PageMetadataProps>({ description, title });
  latestMetadata.current = { description, title };

  // React 19 hoists metadata during commit. Registering in a passive effect allowed a paint with
  // the provider default (or the previous route's metadata) before the page title arrived. Layout
  // registration flushes the provider update before paint and keeps one stable identity for the
  // lifetime of this position in the metadata tree.
  useLayoutEffect(() => {
    if (!present) {
      return;
    }
    upsert(registrationKey, latestMetadata.current);
    return () => remove(registrationKey);
  }, [present, registrationKey, remove, upsert]);

  // Prop changes update the existing slot rather than unregistering and appending it. That
  // preserves child precedence when an earlier PageMetadata changes while both remain mounted.
  useLayoutEffect(() => {
    if (present) {
      upsert(registrationKey, { description, title });
    }
  }, [description, present, registrationKey, title, upsert]);

  if (present) {
    return null;
  }

  // Isolated rendering outside the application provider brings its own provider, and renders a
  // single resolved instance for the same reason.
  return (
    <HelmetProvider>
      <Helmet
        meta={description ? [{ content: description, name: "description" }] : undefined}
        title={title}
        titleTemplate="%s"
      />
    </HelmetProvider>
  );
}
